import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Building2, Lock, Mail, ShieldCheck } from 'lucide-react';

const SystemLogin = () => {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, reset } = useForm({ shouldUnregister: true });

  useEffect(() => {
    if (isAuthenticated && user?.role === 'system') {
      navigate('/system/tenants', { replace: true });
    } else if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, user]);

  useEffect(() => {
    reset();
  }, [reset]);

  const onSubmit = async (data) => {
    const result = await login(
      {
        email: data.email,
        password: data.password,
      },
      'system'
    );

    if (result.success) {
      navigate('/system/tenants', { replace: true });
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute -top-24 -left-24 w-[36rem] h-[36rem] rounded-full bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.22),_transparent_60%)] blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-[radial-gradient(ellipse_at_center,_rgba(16,185,129,0.20),_transparent_60%)] blur-3xl" />

      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mx-auto h-14 w-14 bg-gradient-to-br from-sky-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-sky-500/20">
            <ShieldCheck className="h-8 w-8 text-white" />
          </div>
          <h1 className="mt-6 text-3xl font-extrabold text-white">System Login</h1>
          <p className="mt-2 text-sm text-slate-300">Administrative access for tenant management</p>
        </div>

        <div className="bg-white/95 backdrop-blur-xl border border-white/30 shadow-2xl rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <div className="h-11 w-11 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
              <Building2 className="h-6 w-6 text-sky-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">System users only</p>
              <p className="text-xs text-slate-500">Use your system email and password</p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                System Email
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  className="input pl-10"
                  placeholder="system@company.com"
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', { required: 'Password is required' })}
                  type="password"
                  className="input pl-10"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
            >
              Sign In
            </button>

            <div className="flex items-center justify-between gap-3 pt-2 text-sm">
              <Link to="/login" className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium">
                <ArrowLeft className="w-4 h-4" />
                Back to main login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SystemLogin;